/**
 * Life4Earth V2 — Workflow Requests API Adapter
 *
 * Replaces: localStorage key l4e_workflow_requests
 * Endpoint: /api/WorkflowRequests
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface WorkflowRequestResponse {
  id: string;
  workflowGuid: string;
  title: string | null;
  description: string | null;
  targetType: string;
  targetGuid: string;
  actionType: string;
  payload: Record<string, any> | null;
  status: string;
  version: number;
  createdBy: string | null;
  submittedBy: string | null;
  assignedToGuids: string[];
  history: any[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowRequestPayload {
  workflowGuid: string;
  title?: string;
  description?: string;
  targetType: string;
  targetGuid: string;
  actionType: string;
  payload?: Record<string, any>;
  status?: string;
  version?: number;
  submittedBy?: string;
  assignedToGuids?: string[];
  history?: any[];
}

// ── API Functions ────────────────────────────────────────────

export async function fetchAllWorkflowRequests(
  status?: string
): Promise<WorkflowRequestResponse[]> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const qs = params.toString();
  return apiFetch<WorkflowRequestResponse[]>(
    `/api/WorkflowRequests/all${qs ? `?${qs}` : ""}`
  );
}

export async function fetchWorkflowsByTarget(
  targetGuid: string,
  targetType?: string
): Promise<WorkflowRequestResponse[]> {
  const params = new URLSearchParams({ targetGuid });
  if (targetType) params.set("targetType", targetType);
  return apiFetch<WorkflowRequestResponse[]>(`/api/WorkflowRequests?${params.toString()}`);
}

export async function upsertWorkflowRequest(
  request: CreateWorkflowRequestPayload
): Promise<WorkflowRequestResponse> {
  return apiFetch<WorkflowRequestResponse>("/api/WorkflowRequests", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function batchUpsertWorkflowRequests(
  items: CreateWorkflowRequestPayload[]
): Promise<WorkflowRequestResponse[]> {
  return apiFetch<WorkflowRequestResponse[]>("/api/WorkflowRequests/batch", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export async function updateWorkflowStatus(
  workflowGuid: string,
  status: string,
  version?: number
): Promise<WorkflowRequestResponse> {
  return apiFetch<WorkflowRequestResponse>(`/api/WorkflowRequests/${workflowGuid}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, version }),
  });
}

export async function deleteWorkflowRequest(workflowGuid: string): Promise<void> {
  return apiFetch<void>(`/api/WorkflowRequests/${workflowGuid}`, {
    method: "DELETE",
  });
}

/** Convert API response to the localStorage shape used by workflowStore.ts */
export function workflowResponseToStoreShape(r: WorkflowRequestResponse): Record<string, any> {
  return {
    workflow_guid: normalizeGuid(r.workflowGuid),
    title: r.title || undefined,
    description: r.description || undefined,
    target_type: r.targetType,
    target_guid: normalizeGuid(r.targetGuid),
    action_type: r.actionType,
    payload: r.payload || {},
    status: r.status,
    version: r.version,
    created_by: normalizeGuid(r.createdBy),
    submitted_by: normalizeGuid(r.submittedBy),
    assigned_to_guids: (r.assignedToGuids || []).map(normalizeGuid),
    history: Array.isArray(r.history) ? r.history : [],
    created_at: r.createdAt,
    updated_at: r.updatedAt,
  };
}
